/**
 * The unreachable-fallback warning (D175, widened D184): a conditional whose
 * variants between them exhaust every case can never fall through.
 *
 * @remarks
 * Variants are ordered and first match wins, and the `fallback` is what is
 * said when every variant misses (D140's shape). When one variant is gated
 * on `x` and another on `not x` — the same condition, structurally — one of
 * the two always holds, so the fallback is never read, and neither is any
 * variant listed after the pair. The author wrote three readings and
 * shipped two. The loader cannot know which of the three was the mistake
 * (the fallback may be a leftover, or the negated variant may have been
 * meant as a narrower case), so this is a warning that names the pair and
 * the path, never a failure.
 *
 * Three shapes exhaust a conditional, and all three are one rule — "some
 * variant's condition, or some pair of them, cannot miss" (Calder's audit of
 * the gates, 2026-09-14 §2; the first two taken as asked, the third left):
 *
 * 1. **A condition and its `not`** — the founding case.
 * 2. **Opposite values of one flag** — `flag_equals(f, true)` beside
 *    `flag_equals(f, false)`: the same pair, spelled without a `not`.
 * 3. **A self-exhausting `any_of`** — one variant gated on
 *    `any_of[x, not x]` (or on an `any_of` holding such an `any_of`). It
 *    always holds, so it strands the fallback and every later variant on
 *    its own.
 *
 * Not taken: a broader earlier condition shadowing a narrower later one
 * (`x` before `all_of[x, y]`). "Is this condition implied by that one" is a
 * decision procedure, and wants a design rather than an afternoon.
 *
 * Structural, like every walk here: a conditional is any object with a
 * `fallback` and a `variants` array whose entries carry a `condition` (text
 * and ambience) or a `when` (narratives). Conditions are compared as
 * canonical JSON — keys sorted, so two authors' orderings of the same
 * condition compare equal — and a condition's COMPLEMENTS are exactly the
 * `not` wrapper and, for a bare `flag_equals`, the other value. Anything
 * cleverer (De Morgan over nested combinators) would be a theorem prover,
 * and a warning that fires on the plain cases catches the plain mistakes.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import { anchorPath } from './anchored-path.js';
import { fieldOf, walkDocumentObjects } from './structural-walk.js';

/**
 * Why the fallback cannot be reached: a pair of variants that between them
 * cannot miss (`negated_pair` — a condition and its `not`;
 * `opposite_flag_values` — one flag read both ways), or one variant that
 * cannot miss on its own (`exhaustive_any_of`).
 */
export type FallbackUnreachableCause =
	| { readonly conditionIndex: number; readonly flagId: string; readonly kind: 'opposite_flag_values'; readonly negationIndex: number }
	| { readonly conditionIndex: number; readonly kind: 'negated_pair'; readonly negationIndex: number }
	| { readonly kind: 'exhaustive_any_of'; readonly variantIndex: number };

/** One conditional whose fallback cannot be reached. */
export interface FallbackUnreachable {
	/** The conditional's path, anchored on the entity's id (`rooms[room_gate].description`). */
	readonly anchoredPath: string;
	/** What exhausts the conditional. */
	readonly cause: FallbackUnreachableCause;
	/** The conditional's path (`rooms[2].description`). */
	readonly path: string;
	/** How many variants follow the exhausting variant(s) — unreachable with the fallback. */
	readonly strandedVariantCount: number;
}

/**
 * A value as canonical JSON: object keys sorted at every depth, so equal
 * conditions serialize equal whatever order they were authored in.
 *
 * @param value - Any JSON-ish value
 * @returns Its canonical text
 */
const canonicalJson = (value: unknown): string => {
	if (Array.isArray(value)) {
		return `[${value.map(canonicalJson).join(',')}]`;
	}

	if (typeof value === 'object' && value !== null) {
		const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right));

		return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(',')}}`;
	}

	// `String(...)` rather than a guard. `JSON.stringify` answers `undefined`
	// for a value that is not JSON at all — which here means a variant entry
	// carrying no condition under either key — and the TEXT "undefined" is a
	// sentinel no `not` wrapper can ever equal, so such an entry simply never
	// pairs. A guard would say the same thing in a branch that a parsed pack
	// can never enter, and so could never be tested.
	return String(JSON.stringify(value));
};

/** A variant's condition, whichever key its shape uses; nothing when the entry is not a variant. */
const conditionOf = (variant: unknown): unknown => {
	return fieldOf(variant, 'condition') ?? fieldOf(variant, 'when');
};

/** The canonical text of `not x`, for a condition `x`. */
const negatedOf = (canonical: string): string => `{"condition":${canonical},"type":"not"}`;

/** The flag a bare `flag_equals` reads, or nothing for any other condition. */
const flagReadOf = (condition: unknown): { readonly expectedValue: boolean; readonly flagId: string } | undefined => {
	const flagId = fieldOf(condition, 'flagId');
	const expectedValue = fieldOf(condition, 'expectedValue');

	return fieldOf(condition, 'type') === 'flag_equals' && typeof flagId === 'string' && typeof expectedValue === 'boolean' ? { expectedValue, flagId } : undefined;
};

/** A pair of conditions that between them cannot miss, and how. */
type ExhaustivePair =
	| { readonly conditionIndex: number; readonly flagId: string; readonly kind: 'opposite_flag_values'; readonly negationIndex: number }
	| { readonly conditionIndex: number; readonly kind: 'negated_pair'; readonly negationIndex: number };

/**
 * The first pair in a list of conditions where one is the other's
 * complement: its exact `not`, or the same flag read for the other value.
 *
 * @param conditions - The conditions, in order
 * @returns The pair's indexes and what makes them complements, or nothing
 */
const exhaustivePairOf = (conditions: readonly unknown[]): ExhaustivePair | undefined => {
	// Each condition read once, both ways it can be compared.
	const readings = conditions.map((condition) => ({ canonical: canonicalJson(condition), flagRead: flagReadOf(condition) }));

	for (const [conditionIndex, { canonical, flagRead }] of readings.entries()) {
		const negationIndex = readings.findIndex((candidate, index) => index !== conditionIndex && candidate.canonical === negatedOf(canonical));

		if (negationIndex !== -1) {
			return { conditionIndex, kind: 'negated_pair', negationIndex };
		}

		const oppositeIndex =
			flagRead === undefined
				? -1
				: readings.findIndex(
					(candidate, index) =>
						index !== conditionIndex && candidate.flagRead?.flagId === flagRead.flagId && candidate.flagRead.expectedValue !== flagRead.expectedValue,
				);

		if (flagRead !== undefined && oppositeIndex !== -1) {
			return { conditionIndex, flagId: flagRead.flagId, kind: 'opposite_flag_values', negationIndex: oppositeIndex };
		}
	}

	return undefined;
};

/**
 * Whether a condition cannot miss on its own: an `any_of` holding a
 * complementary pair, or holding a member that cannot miss.
 *
 * @param condition - Any condition
 * @returns Whether it always holds
 */
const isExhaustive = (condition: unknown): boolean => {
	const members = fieldOf(condition, 'conditions');

	return fieldOf(condition, 'type') === 'any_of' && Array.isArray(members) && (exhaustivePairOf(members) !== undefined || members.some(isExhaustive));
};

/**
 * What exhausts a conditional's variants, if anything: a complementary pair
 * first (the founding shape), else the first variant that cannot miss.
 *
 * @param conditions - Each variant's condition, in order
 * @returns The cause and the index of the last variant that can still be read, or nothing
 */
const causeOf = (conditions: readonly unknown[]): { readonly cause: FallbackUnreachableCause; readonly lastReadableIndex: number } | undefined => {
	const pair = exhaustivePairOf(conditions);

	if (pair !== undefined) {
		return { cause: pair, lastReadableIndex: Math.max(pair.conditionIndex, pair.negationIndex) };
	}

	const variantIndex = conditions.findIndex(isExhaustive);

	return variantIndex === -1 ? undefined : { cause: { kind: 'exhaustive_any_of', variantIndex }, lastReadableIndex: variantIndex };
};

/**
 * Every conditional in the pack whose variants exhaust every case, in
 * document order — one report per conditional, for the first cause found.
 *
 * @param contentPack - The parsed pack
 * @returns The unreachable fallbacks, with their paths
 */
export const findUnreachableFallbacks = (contentPack: ContentPackDefinition): readonly FallbackUnreachable[] => {
	const found: FallbackUnreachable[] = [];

	walkDocumentObjects(contentPack, (record, path) => {
		const { fallback, variants } = record;

		if (fallback === undefined || !Array.isArray(variants)) {
			return;
		}

		const conditions = variants.map((variant: unknown) => conditionOf(variant));
		const exhaustion = causeOf(conditions);

		if (exhaustion !== undefined) {
			found.push({
				anchoredPath: anchorPath(contentPack, path),
				cause: exhaustion.cause,
				path,
				strandedVariantCount: conditions.length - 1 - exhaustion.lastReadableIndex,
			});
		}
	});

	return found;
};
